import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery } from "react-query";
import { useLocation } from 'react-router-dom'
import axios from 'axios';
import { useTable } from 'react-table';
import { AppContext } from "../AppContext";
import { hasAccess, hasRoles, ROLES } from '../utils/AccessLevels';
import { useNavigate } from "react-router-dom";
import { Modal, Button, notification, Popover, Spin, Descriptions, Divider, Image, Tag, Tooltip } from 'antd';
import './Shifts.css';
import tr from '../i18n';
import 'react-image-lightbox/style.css';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import {
    SearchOutlined, PlusCircleOutlined, InfoCircleOutlined, CoffeeOutlined, LoginOutlined, LogoutOutlined, EnvironmentOutlined,
} from "@ant-design/icons";
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MarkerIcon from "../assets/map_marker.png"
import { BASE_MAPS, MAP_ATTRIBUTION_URL } from "../utils/MapUtils";
import { formatUtcWithOffset } from "../utils/DateFormatter";



// Fix for the default marker icon not showing
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ShiftTable = () => {
    const shiftReportDataRef = useRef([]);
    const userDataRef = useRef([]);
    const [tenantId, setTenantId] = useState('');
    const [shiftReportData, setShiftReportData] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    const [gardenFilter, setGardenFilter] = useState('');
    const [isEntryGalleryOpen, setIsEntryGalleryOpen] = useState(false);
    const [isExitGalleryOpen, setIsExitGalleryOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [shiftDetailsModalVisibility, setShiftDetailsModalVisibility] = useState(false);
    const [shiftMapVisibility, setShiftMapVisibility] = useState("");
    const [activeShift, setActiveShift] = useState(null);
    const [activeMarkers, setActiveMarkers] = useState(null);
    const [activeRow, setActiveRow] = useState(null);
    const [userData, setUserData] = useState(null);
    const [refreshTable, setRefreshTable] = useState(false);
    const [loading, setLoading] = useState(false);
    const { gardens, fetchAllGardens, role } = useContext(AppContext);
    const [mapCenter, setMapCenter] = useState([41.084, 29.142205]);
    const [selectedTileUrl, setSelectedTileUrl] = useState(BASE_MAPS[0].value);
    const customMarker = new L.Icon({
        iconUrl: MarkerIcon,
        iconSize: [32, 32], // Set the size of the icon
        iconAnchor: null, // Set the point of the icon to be anchored (centered at the bottom)
        popupAnchor: [0, -32], // Set the point from which the popup will appear
    });
    const location = useLocation();
    const navigate = useNavigate();
    const { initGardenId } = location.state || {};
    const hostname = window.location.hostname
    const ALLOWED_HOSTNAMES = ['bilisimvadisi.akillisula.com', 'bilisimvadisi.grungard.ai', 'darica.akillisula.com', 'darica.grungard.ai', 'daricazabita.akillisula.com',
        'daricazabita.grungard.ai']

    useEffect(() => {
        try {
            if (!hasAccess(ROLES.FIELD_MANAGER, role) || !(hostname && ALLOWED_HOSTNAMES.includes(hostname))) {
                navigate('/control-center/gardens');
            }

            fetchAllGardens()
            const userSettings = JSON.parse(localStorage.getItem("settings"));
            const parsedTenantId = userSettings?.title?.toLowerCase().replace(' ', '');
            const derivedTenantId = hostname.split('.')[0] || parsedTenantId;
            setTenantId(derivedTenantId);
        } catch (error) {
            console.error(error);
        }
    }, [])

    function MapController({ visible, center, zoom = 13 }) {
        const map = useMap();

        useEffect(() => {
            if (visible && center) {
                setTimeout(() => {
                    map.invalidateSize();
                    map.setView(center, zoom, { animate: false });
                }, 0);
            }
        }, [visible, center, zoom, map]);

        return null;
    }

    const columns = useMemo(() => [
        { Header: tr('Employee Name'), accessor: 'userName' },
        { Header: tr('Garden Name'), accessor: 'gardenName' },
        {
            Header: tr('Status'),
            accessor: 'endTime',
            Cell: ({ row }) => renderStatusCell(row),
        },
        {
            Header: tr('Shift Start'),
            accessor: 'startTime',
            Cell: ({ row }) => renderDateCell(row, true),
        },
        {
            Header: tr('Shift End'),
            accessor: 'formattedEnd',
            Cell: ({ row }) => renderDateCell(row, false),
        },
        {
            Header: tr('Details'),
            accessor: '_id',
            Cell: ({ row }) => {
                const [popoverVisible, setPopoverVisible] = useState(false);

                return (
                    <div className="flex flex-row gap-4 items-center">
                        {/* Details button */}
                        <Tooltip title={tr("Details")}>
                            <Button
                                type="text"
                                icon={<InfoCircleOutlined style={{ fontSize: 18 }} />}
                                onClick={() => handleCellClick(row.original._id, row)}
                            />
                        </Tooltip>
                    </div >)
            }
        },
    ], []);

    const userSettings = useQuery(["userSettings"], () =>
        axios.get(
            process.env.REACT_APP_GARDENER_API_END_POINT +
            `/user/settings`
        ), {
        onSuccess: (data) => {
            if (data?.data?.data?.id && data?.data?.data?.name) {
                setUserData({ id: data?.data?.data?.id, name: data?.data?.data?.name });
                userDataRef.current = { id: data?.data?.data?.id, name: data?.data?.data?.name };
            }
        },
        enabled: true,
        refetchOnWindowFocus: false
    });

    const fetchReportData = useMutation(data =>
        axios.post(`${process.env.REACT_APP_EMPLOYEE_API_END_POINT}/shifts/report`, data),
        {
            onMutate: () => setLoading(true),
            onSuccess: data => {
                if (data?.data?.success && data.data.data?.shifts) {
                    setShiftReportData(data.data.data.shifts);
                    shiftReportDataRef.current = data.data.data.shifts;
                }
            },
            onError: error => {
                console.error("Error fetching report data:", error);
            },
            onSettled: () => setLoading(false)
        }
    );


    useEffect(() => {
        if (!initGardenId || initGardenId === '0') return;

        setGardenFilter(initGardenId)

    }, [initGardenId])

    useEffect(() => {
        if (tenantId) {
            if (gardenFilter !== null && gardenFilter !== '') {
                fetchReportData.mutate({ tenantId: tenantId, gardenId: gardenFilter });
            } else {
                fetchReportData.mutate({ tenantId: tenantId });
            }
        }
    }, [tenantId, gardenFilter]);


    const filteredShiftData = useMemo(() => {
        const filtered = shiftReportData.filter(row =>
            row.userName.toLowerCase().includes(searchFilter.toLowerCase())
        );
        return filtered;
    }, [shiftReportData, searchFilter]);

    const handleCellClick = (shiftId, row) => {
        const selectedShift = shiftReportDataRef.current.find(shift => shift._id === shiftId);
        if (selectedShift) {
            setActiveShift(selectedShift);
            setActiveRow(row)
            setShiftDetailsModalVisibility(true);
        } else {
        }
    };

    const renderStatusCell = (row, largeTxt) => {
        const isCompleted = row.values.endTime;
        let status = tr('In Progress');
        let iconColor = "#FF9500";

        if (isCompleted) {
            iconColor = "#34C759";
            status = tr('Completed');
        }

        return (
            <div className="flex flex-row items-center gap-2">
                {status}
                <div className="circular-icon" style={{ backgroundColor: iconColor }} />
            </div>
        );
    };

    const renderDateCell = (row, isStart) => {
        if (!row.values.endTime && !isStart) return (
            <div className="flex flex-row items-center gap-2"></div>
        );
        const formattedDate = isStart ? formatUtcWithOffset(row.values.startTime, 3) : formatUtcWithOffset(row.values.endTime, 3);
        return (
            <div className="flex flex-row items-center gap-2">
                {formattedDate}
            </div>
        );
    }

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
        columns,
        data: filteredShiftData,
    });

    return (
        <div className="table-wrapper">
            <Spin spinning={loading} tip={tr('Loading')}>
                <div className="filter-container flex flex-row justify-between items-center">
                    <div className='flex flex-row gap-3'>
                        <input
                            type="text"
                            value={searchFilter}
                            onChange={(e) => { setSearchFilter(e.target.value) }}
                            placeholder={tr('Search')}
                            className="search-bar"
                        />
                        <select
                            value={gardenFilter}
                            onChange={(e) => { setGardenFilter(e.target.value) }}
                            className="dropdown"
                        >
                            <option value="">{tr('All Gardens')}</option>
                            {gardens.map((garden, index) => (
                                <option key={index} value={garden.id}>
                                    {garden.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <table {...getTableProps()} className="styled-table">
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()} className="table-row">
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <Modal
                    visible={shiftDetailsModalVisibility}
                    onCancel={() => setShiftDetailsModalVisibility(false)}
                    footer={[
                        <Button key="close" onClick={() => setShiftDetailsModalVisibility(false)}>
                            {tr('Close')}
                        </Button>,
                    ]}
                    width="30%"
                    style={{ top: 60 }}
                    bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                >
                    {activeShift &&
                        <>
                            {/* Employee Info */}
                            <Descriptions
                                bordered
                                size="small"
                                column={1}
                                title={tr('Shift Details')}
                            >
                                <Descriptions.Item label={tr("Employee Name")}>
                                    {activeShift?.userName}
                                </Descriptions.Item>

                                {activeRow && (
                                    <Descriptions.Item label={tr("Status")}>
                                        {renderStatusCell(activeRow)}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>

                            {/* Entry Section */}
                            <Divider orientation="left">
                                <LoginOutlined /> {tr("Entry")}
                            </Divider>
                            <Descriptions bordered size="small" column={1}>
                                <Descriptions.Item label={tr("Shift Start")}>
                                    {formatUtcWithOffset(activeShift?.startTime, 3)}
                                </Descriptions.Item>

                                {activeShift?.entryNotes?.some((str) => str !== "") && (
                                    <Descriptions.Item label={tr("Entry Notes")}>
                                        {activeShift.entryNotes.join(", ")}
                                    </Descriptions.Item>
                                )}

                                {activeShift.entryLat && activeShift.entryLng && (
                                    <Descriptions.Item label={tr("Entry Location")}>
                                        <Button
                                            type="link"
                                            icon={<EnvironmentOutlined />}
                                            onClick={() => {
                                                setMapCenter([activeShift.entryLat, activeShift.entryLng]);
                                                setActiveMarkers([
                                                    {
                                                        position: [activeShift.entryLat, activeShift.entryLng],
                                                        description: tr("Entry Location"),
                                                    },
                                                ]);
                                                setShiftMapVisibility("entry");
                                            }}
                                        >
                                            {tr("View on Map")}
                                        </Button>
                                        <Tag color={activeShift.entryInGarden ? "green" : "red"}>
                                            {activeShift.entryInGarden
                                                ? tr('Inside the Garden')
                                                : tr('Outside of the Garden')}
                                        </Tag>
                                    </Descriptions.Item>
                                )}

                                {activeShift?.entryImages?.some((str) => str !== "") && (
                                    <Descriptions.Item label={tr("Entry Images")}>
                                        <Image.PreviewGroup
                                            preview={{ getContainer: () => document.body }}
                                        >
                                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                                {activeShift.entryImages.map((image, i) => (
                                                    <Image
                                                        key={i}
                                                        width={100}
                                                        src={image}
                                                        style={{ borderRadius: 8 }}
                                                        preview={{
                                                            mask: tr('Preview'),
                                                            getContainer: () => document.body
                                                        }}
                                                        alt={`Entry ${i + 1}`}
                                                    />
                                                ))}
                                            </div>
                                        </Image.PreviewGroup>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>

                            {/* Breaks Section */}
                            {activeShift?.breaks?.length > 0 && (
                                <>
                                    <Divider orientation="left">
                                        <CoffeeOutlined /> {tr("Breaks")}
                                    </Divider>
                                    <Descriptions bordered size="small" column={1}>
                                        {activeShift.breaks.map((br, idx) => (
                                            <React.Fragment key={idx}>
                                                <Descriptions.Item label={tr("Break Start")}>
                                                    {formatUtcWithOffset(br?.startTime, 3)}
                                                </Descriptions.Item>
                                                {br?.endTime && (
                                                    <Descriptions.Item label={tr("Break End")}>
                                                        {formatUtcWithOffset(br?.endTime, 3)}
                                                    </Descriptions.Item>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </Descriptions>
                                </>
                            )}

                            {activeShift?.endTime && (
                                <>
                                    <Divider orientation="left">
                                        <LogoutOutlined /> {tr("Exit")}
                                    </Divider>
                                    <Descriptions bordered size="small" column={1}>
                                        <Descriptions.Item label={tr("Shift End")}>
                                            {formatUtcWithOffset(activeShift?.endTime, 3)}
                                        </Descriptions.Item>

                                        {activeShift?.exitNotes?.some((str) => str !== "") && (
                                            <Descriptions.Item label={tr("Exit Notes")}>
                                                {activeShift.exitNotes.join(", ")}
                                            </Descriptions.Item>
                                        )}

                                        {activeShift.exitLat &&
                                            activeShift.exitLng &&
                                            (activeShift.exitLat !== 0 || activeShift.exitLng !== 0) && (
                                                <Descriptions.Item label={tr("Exit Location")}>
                                                    <Button
                                                        type="link"
                                                        icon={<EnvironmentOutlined />}
                                                        onClick={() => {
                                                            setMapCenter([activeShift.exitLat, activeShift.exitLng]);
                                                            setActiveMarkers([
                                                                {
                                                                    position: [activeShift.exitLat, activeShift.exitLng],
                                                                    description: tr("Exit Location"),
                                                                },
                                                            ]);
                                                            setShiftMapVisibility("exit");
                                                        }}
                                                    >
                                                        {tr("View on Map")}
                                                    </Button>
                                                    <Tag color={activeShift.exitInGarden ? "green" : "red"}>
                                                        {activeShift.exitInGarden
                                                            ? tr('Inside the Garden')
                                                            : tr('Outside of the Garden')}
                                                    </Tag>
                                                </Descriptions.Item>
                                            )}

                                        {activeShift?.exitImages?.some((str) => str !== "") && (
                                            <Descriptions.Item label={tr("Exit Images")}>
                                                <Image.PreviewGroup
                                                    preview={{ getContainer: () => document.body }}
                                                >
                                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                                        {activeShift.exitImages.map((image, i) => (
                                                            <Image
                                                                key={i}
                                                                width={100}
                                                                src={image}
                                                                style={{ borderRadius: 8 }}
                                                                preview={{
                                                                    mask: tr('Preview'),
                                                                    getContainer: () => document.body
                                                                }}
                                                                alt={`Exit ${i + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                </Image.PreviewGroup>
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                </>
                            )}
                        </>
                    }
                </Modal>

                <Modal
                    title={shiftMapVisibility === "entry" ? tr('Entry Location') : tr('Exit Location')}
                    visible={shiftMapVisibility !== ""}
                    onCancel={() => {
                        setShiftMapVisibility("")
                    }}
                    footer={[
                        <Button key="close" onClick={() => setShiftMapVisibility("")}>
                            {tr('Close')}
                        </Button>,
                    ]}
                    width="50%"
                    style={{ top: 60 }}
                    bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                >
                    <>
                        {activeMarkers != null &&
                            <>
                                <MapContainer center={mapCenter} zoom={13} style={{ height: '400px', width: '100%' }}>
                                    <MapController
                                        visible={shiftMapVisibility}
                                        center={mapCenter}
                                        zoom={13}
                                    />
                                    <TileLayer
                                        url={selectedTileUrl}
                                        attribution={MAP_ATTRIBUTION_URL}
                                    />
                                    {activeMarkers.map((marker, index) => (
                                        <>
                                            {marker.position &&
                                                <Marker key={index} position={marker.position} icon={customMarker}>
                                                    <Popup>{marker.description}</Popup>
                                                </Marker>}
                                        </>
                                    ))}
                                </MapContainer>
                            </>
                        }
                    </>
                </Modal>
            </Spin>
        </div>
    );
};

function formatTimeDifference(startTime, endTime) {
    const diffInMs = endTime - startTime;

    const totalSeconds = Math.floor(diffInMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Build the result string conditionally to skip zero values
    const parts = [];
    if (hours > 0) parts.push(`${hours} ${tr('Hours')}`);
    if (minutes > 0) parts.push(`${minutes} ${tr('Minutes')}`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds} ${tr('Seconds')}`); // Include seconds even if it's 0, if it's the only part

    return parts.join(' ');
}

function formatDateTime(dateTimeString) {
    const date = new Date(dateTimeString);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear().toString();
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const seconds = date.getUTCSeconds().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
}

export default ShiftTable;
